import React, { useCallback, useState, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Slider, ThemeProvider } from "@mui/material";

import {
  CodeDetection,
  ConfidenceLevels,
  VideoAnalysisService,
} from "../../../../../services/inspection/VideoAnalysisService";
import { translationService } from "../../../../../services/translation/TranslationService";
import { MUIComponentsTheme, useTheme } from "../../../../../services/theme-service/ThemeService";
import Button from "../../../../../components/button/Button";
import "./Tools.scss";
import CodeDetectionButton from "../../../../../components/buttons/code-detection-button/CodeDetectionButton";
import { Dropdown } from "../../../../../components/dropdown/Dropdown";
import AnalysisIndivididualConfidenceModal from "./AnalysisIndivididualConfidenceModal";
import { AnalysisConfidenceStorage } from "../../../../../services/organization-analysis-confidence/AnalysisConfidenceStorage";
import { Edit } from "../../../../../components/svg/svgImageList";
import Tooltip from "../../../../../components/tooltip/Tooltip";
import { featureFlagsStorage } from "../../../../../services/feature-flags/FeatureFlagsStorage";
import { Flags } from "../../../../../generated/FeatureFlags";

interface ToolsProps {
  videoAnalysisService: VideoAnalysisService;
  analysisConfidenceStorage: AnalysisConfidenceStorage;
}

enum ToolsTabs {
  Overview,
  Characterization,
}

export enum ConfidenceOptions {
  Recommended = 0,
  Overall = 1,
  Individual = 2,
}

export default observer(function Tools(props: ToolsProps) {
  const { videoAnalysisService, analysisConfidenceStorage } = props;
  const { themeClass } = useTheme();

  const [tabSelection, setTabSelection] = useState<ToolsTabs>(ToolsTabs.Overview);
  const isOverviewSelected = useMemo(() => tabSelection === ToolsTabs.Overview, [tabSelection]);
  const isCharacterizationSelected = useMemo(
    () => tabSelection === ToolsTabs.Characterization,
    [tabSelection]
  );

  const handleOverviewClick = useCallback(() => {
    setTabSelection(ToolsTabs.Overview);
    videoAnalysisService.changeView(true);
  }, [videoAnalysisService]);

  const handleCharacterizationsClick = useCallback(() => {
    setTabSelection(ToolsTabs.Characterization);
    videoAnalysisService.changeView(false);
  }, [videoAnalysisService]);

  const handleMainCodeClick = useCallback(
    (codeDetection: CodeDetection) => {
      if (!codeDetection.isDetected) return;

      videoAnalysisService.changeMainCodeVisibility(codeDetection.code);
      videoAnalysisService.toggleMainCode(codeDetection.code, !codeDetection.isVisible!);
    },
    [videoAnalysisService]
  );

  const marks = [
    {
      value: 65,
      label: "65%",
    },
    {
      value: 70,
      label: "70%",
    },
    {
      value: 75,
      label: "75%",
    },
    {
      value: 80,
      label: "80%",
    },
    {
      value: 85,
      label: "85%",
    },
    {
      value: 90,
      label: "90%",
    },
    {
      value: 95,
      label: "95%",
    },
    {
      value: 100,
      label: "100%",
    },
  ];

  const mainCodesGroupedByGroupCode = useMemo(() => {
    return videoAnalysisService.detectedMainCodes
      .filter((c) => c.isDetected)
      .slice()
      .reverse()
      .reduce((group: any, detectedMainCode: any) => {
        group[detectedMainCode.groupCode!] = group[detectedMainCode.groupCode!] ?? [];
        group[detectedMainCode.groupCode!].push(detectedMainCode);
        return group;
      }, {});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(videoAnalysisService.detectedMainCodes)]);

  const getConnecterColor = useCallback(
    (groupCode: string, mainCodeIndex: number): string | undefined => {
      const currentCode: CodeDetection = mainCodesGroupedByGroupCode[groupCode][mainCodeIndex];
      const nextCode: CodeDetection = mainCodesGroupedByGroupCode[groupCode][mainCodeIndex + 1];

      if (currentCode.isDetected) {
        return currentCode.colorCode;
      }

      if (nextCode?.isDetected) {
        return nextCode.colorCode;
      }

      return undefined;
    },
    [mainCodesGroupedByGroupCode]
  );

  const handleConfidenceChange = useCallback(
    async (newValue: any) => {
      videoAnalysisService.setAnalysisConfidenceOption(newValue.id);
      if (videoAnalysisService.firstTimeChoosingAnalysisIndividualConfidence) {
        const confidenceLevels: ConfidenceLevels = {};

        if (!analysisConfidenceStorage.AnalysisLevels.length) {
          await analysisConfidenceStorage.load();
        }

        analysisConfidenceStorage.AnalysisLevels.forEach((code) => {
          confidenceLevels[code.name] = code.level / 100;
        });

        videoAnalysisService.setIndividualAnalysisConfidence(confidenceLevels);
      }
    },
    [analysisConfidenceStorage, videoAnalysisService]
  );

  const confidenceOptions = useMemo(() => {
    return [
      {
        id: ConfidenceOptions.Recommended,
        name: translationService.translate("Analysis.confidence-option.recommended"),
      },
      {
        id: ConfidenceOptions.Overall,
        name: translationService.translate("Analysis.confidence-option.overall"),
      },
      {
        id: ConfidenceOptions.Individual,
        name: translationService.translate("Analysis.confidence-option.individual"),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationService.language]);

  const handleOverallConfidenceChange = useCallback(
    (_: any, value: number | number[]) => {
      videoAnalysisService.setAnalysisOverallConfidence((value as number) / 100);
    },
    [videoAnalysisService]
  );

  const individualConfidenceText = useMemo(() => {
    const confidenceValues = Object.values(videoAnalysisService.analysisIndividualConfidenceLevels);

    return (
      <div className="individual-confidence-container">
        <div className="individual-confidence-value">
          {confidenceValues.length !== 0 &&
            (new Set(confidenceValues).size === 1
              ? translationService.translate("analysis.individual-confidence.value", {
                  value: confidenceValues[0] * 100,
                })
              : translationService.translate("analysis.individual-confidence.values", {
                  minValue: Math.min(...confidenceValues) * 100,
                  maxValue: Math.max(...confidenceValues) * 100,
                }))}
        </div>
        <Edit
          className={themeClass(["icon-edit"])}
          onClick={() => {
            videoAnalysisService.showAnalysisIndivididualConfidenceModal();
          }}
        />
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    themeClass,
    videoAnalysisService.analysisIndividualConfidenceLevels,
    translationService.language,
  ]);

  const getDisplayedDamagesInfo = () => {
    return (
      <div>
        {translationService.translate("general.damages")}
        {" ("}
        {translationService.translate("Analysis.damages-found-label-part1", {
          detectedDamagesCount: videoAnalysisService.detectedMainCodes.filter((c) => c.isDetected)
            .length,
        })}
        <Tooltip
          boldedText="general.main-codes"
          title={videoAnalysisService.detectedMainCodes
            ?.map((mainCode) => mainCode.code)
            .join(", ")}
        >
          <span className="underlined-text">
            {translationService.translate("Analysis.damages-found-label-part2", {
              totalDamagesCount: videoAnalysisService.detectedMainCodes.length,
            })}
          </span>
        </Tooltip>
        {")"}
      </div>
    );
  };

  return videoAnalysisService.isLoading || !videoAnalysisService.analysisIsAvailable ? (
    <></>
  ) : (
    <div className="tools-container">
      <div className="tools-label">{translationService.translate("Analysis.tools")}</div>

      <div className="tools-content-container">
        <div className="tools-section">
          <div className="buttons-container">
            <Button
              textKey="general.overview"
              onClick={handleOverviewClick}
              className={themeClass(["btn-big", isOverviewSelected ? "" : "fill-color-ghost"])}
            />
            <Button
              textKey="Analysis.legend-characterizations"
              onClick={handleCharacterizationsClick}
              className={themeClass([
                "btn-big",
                isCharacterizationSelected ? "" : "fill-color-ghost",
              ])}
            />
          </div>
        </div>

        <div className="tools-section detections-with-color-container">
          <>
            {getDisplayedDamagesInfo()}
            <div className="codes-container">
              {Object.keys(mainCodesGroupedByGroupCode).map((key) => (
                <div key={key} className="grouping">
                  {mainCodesGroupedByGroupCode[key].map(
                    (mainCode: CodeDetection, index: number) => (
                      <div key={mainCode.code} className="code-container">
                        <CodeDetectionButton
                          key={mainCode.code}
                          codeDetection={mainCode}
                          handleClick={handleMainCodeClick}
                        />
                        {mainCodesGroupedByGroupCode[key][index + 1] && (
                          <span
                            className="connecter"
                            style={{ color: getConnecterColor(key, index) }}
                          />
                        )}
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>
          </>
        </div>

        {isOverviewSelected && (
          <div className="tools-section">
            <div className="pauses">{translationService.translate("Analysis.legend-pauses")}</div>
          </div>
        )}

        {featureFlagsStorage.isFeatureEnabled(Flags.Show_Confidence_Level_Controls) && (
          <div className="tools-section">
            <div className="confidence-container">
              <div className="confidence-option-container">
                <span>{translationService.translate("Analysis.confidence")}</span>
                <Dropdown
                  options={confidenceOptions}
                  selectedOption={
                    confidenceOptions.find(
                      (o) => o.id === videoAnalysisService.analysisConfidenceOption
                    )!
                  }
                  onChange={handleConfidenceChange}
                  fullWidth
                />
              </div>
              {videoAnalysisService.analysisConfidenceOption === ConfidenceOptions.Overall && (
                <div className="slider-container">
                  <ThemeProvider theme={MUIComponentsTheme}>
                    <Slider
                      key={"slider-graph-tools"}
                      value={videoAnalysisService.analysisOverallConfidence * 100}
                      min={65}
                      max={100}
                      marks={marks}
                      step={1}
                      onChangeCommitted={handleOverallConfidenceChange}
                    />
                  </ThemeProvider>
                  <div className="slider-value">
                    {videoAnalysisService.analysisOverallConfidence * 100}%
                  </div>
                </div>
              )}
            {videoAnalysisService.analysisConfidenceOption === ConfidenceOptions.Individual &&
              individualConfidenceText}
            </div>
          </div>
        )}

        {isCharacterizationSelected && (
          <div className="tools-section detections-with-color-container">
            <div className="codes-container">
              {videoAnalysisService.detectedCharacterizations.map((characterizationCode) => {
                return (
                  <div
                    className="detection-color-code"
                    key={characterizationCode.code + characterizationCode.colorCode}
                    style={{ backgroundColor: characterizationCode.colorCode }}
                  >
                    {characterizationCode.code}
                  </div>
                );
              })}
              <div
                className="detection-color-code"
                key={"test"}
                style={{ backgroundColor: "#B3B3B3" }}
              >
                MC
              </div>
            </div>
          </div>
        )}
      </div>
      <AnalysisIndivididualConfidenceModal
        videoAnalysisService={videoAnalysisService}
        analysisConfidenceStorage={analysisConfidenceStorage}
      />
    </div>
  );
});
